import React, { useState } from 'react';

import { Box, SimpleGrid, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { ManageSmartAssistantSectionControlWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantControlWrapper';
import { StaticToolGroupCard } from 'pages/ManageSmartAssistant/Sections/ToolsSection/StaticToolGroupCard';
import { useTranslation } from 'react-i18next';
import { MULTI_INSTANCE_TOOL_GROUP_KINDS } from 'shared/constants/tool-group';
import { StaticToolGroup } from 'shared/interfaces/tools/Tool';

interface ToolsSelectorProps {
  unattachedToolGroups: StaticToolGroup[];
  attachedToolGroups: StaticToolGroup[];
  unavailableToolGroups: StaticToolGroup[];
  onBack: () => void;
  onSelectToolGroup: (toolGroup: StaticToolGroup) => void;
}

export function ToolsSelector({
  unattachedToolGroups,
  attachedToolGroups,
  unavailableToolGroups,
  onBack,
  onSelectToolGroup
}: ToolsSelectorProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState<string>('');

  const filterToolsBySearch = (toolGroups: StaticToolGroup[]) => {
    if (!search) return toolGroups;
    const searchLower = search.toLowerCase();
    return toolGroups.filter(
      (toolGroup) =>
        toolGroup.displayName.toLowerCase().includes(searchLower) ||
        toolGroup.displayDescription.toLowerCase().includes(searchLower)
    );
  };

  const filteredUnattachedToolGroups = filterToolsBySearch(unattachedToolGroups);
  const filteredUnavailableToolGroups = filterToolsBySearch(unavailableToolGroups);
  const filteredAttachedToolGroups = filterToolsBySearch(attachedToolGroups);

  return (
    <ManageSmartAssistantSectionControlWrapper
      title={t('SMART_ASSISTANT.TOOLS_SECTION.SELECTOR.TITLE')}
      onBack={onBack}>
      <Box w="100%">
        <TextInput
          placeholder={t('SMART_ASSISTANT.TOOLS_SECTION.SEARCH_PLACEHOLDER')}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          icon={<IconSearch size={16} />}
          mb="md"
        />
        <SimpleGrid
          cols={3}
          spacing="md"
          breakpoints={[
            { maxWidth: 'md', cols: 2, spacing: 'md' },
            { maxWidth: 'sm', cols: 1, spacing: 'sm' }
          ]}>
          {filteredAttachedToolGroups
            // We only show single instance tool groups in the selector
            .filter((toolGroup) => !MULTI_INSTANCE_TOOL_GROUP_KINDS.includes(toolGroup.kind))
            .map((toolGroup) => (
              <StaticToolGroupCard key={toolGroup.kind} staticToolGroup={toolGroup} isAvailable isAttached />
            ))}
          {filteredUnattachedToolGroups.map((toolGroup) => (
            <StaticToolGroupCard
              key={toolGroup.kind}
              staticToolGroup={toolGroup}
              isAvailable
              isAttached={false}
              onClick={() => onSelectToolGroup(toolGroup)}
            />
          ))}
          {filteredUnavailableToolGroups.map((toolGroup) => (
            <StaticToolGroupCard
              key={toolGroup.kind}
              staticToolGroup={toolGroup}
              isAvailable={false}
              isAttached={false}
            />
          ))}
        </SimpleGrid>
      </Box>
    </ManageSmartAssistantSectionControlWrapper>
  );
}
